/** Waive a statement with a required reason (TMMPDM-0008). */
import { useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';
import { useCopy } from '@/hooks/useCopy';
import { SubmitButton } from '@/components/page';
import { adminApi } from '@/api/tmm/admin';
import { ApiError, errorCopy, isValidationFailed, validationFields } from '@/api/errors';

interface WaiveStatementDialogProps {
  open: boolean;
  slug: string;
  statementId: number;
  onClose: () => void;
  onDone: () => void;
}

export function WaiveStatementDialog({
  open,
  slug,
  statementId,
  onClose,
  onDone,
}: WaiveStatementDialogProps) {
  const { t } = useCopy();
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('statements.waiveDialog.title', 'Waive statement')}</DialogTitle>
      {open ? (
        <WaiveStatementForm
          slug={slug}
          statementId={statementId}
          onClose={onClose}
          onDone={onDone}
        />
      ) : null}
    </Dialog>
  );
}

function WaiveStatementForm({
  slug,
  statementId,
  onClose,
  onDone,
}: Omit<WaiveStatementDialogProps, 'open'>) {
  const { t } = useCopy();
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);

  const submit = async () => {
    const trimmed = reason.trim();
    if (!trimmed) {
      setFieldError(t('statements.waiveDialog.reasonRequired', 'Reason is required.'));
      return;
    }
    setSubmitting(true);
    setError(null);
    setFieldError(null);
    try {
      await adminApi.waiveStatement(slug, statementId, { reason: trimmed });
      onDone();
    } catch (err: unknown) {
      if (err instanceof ApiError && isValidationFailed(err)) {
        const fields = validationFields(err, t);
        setFieldError(fields.reason ?? errorCopy(err, t, ''));
      } else {
        setError(errorCopy(err, t, t('statements.waiveError', "Couldn't waive the statement.")));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error ? <Alert severity="error">{error}</Alert> : null}
          <TextField
            label={t('statements.waiveDialog.reason', 'Reason')}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            error={Boolean(fieldError)}
            helperText={fieldError}
            required
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('subscriptions.subscribeDialog.cancel', 'Cancel')}</Button>
        <SubmitButton onClick={() => void submit()} busy={submitting}>
          {t('statements.waiveDialog.submit', 'Waive')}
        </SubmitButton>
      </DialogActions>
    </>
  );
}
